import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { Truck, ArrowRight, Shield, Clock, BarChart3 } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function LoginPage() {
    const { login, isAuthenticated, loading } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!loading && isAuthenticated) {
            navigate('/dashboard', { replace: true });
        }
    }, [isAuthenticated, loading, navigate]);

    const features = [
        {
            icon: Truck,
            title: 'Load Board Integration',
            description: 'Connect to DAT and Truckstop for real-time load availability'
        },
        {
            icon: BarChart3,
            title: 'Financial Tracking',
            description: 'Monitor revenue, invoices, and payments in one place'
        },
        {
            icon: Clock,
            title: 'Real-time Updates',
            description: 'Track shipments and get instant status notifications'
        },
        {
            icon: Shield,
            title: 'Secure & Reliable',
            description: 'Enterprise-grade security for your logistics data'
        }
    ];

    return (
        <div className="login-bg min-h-screen flex">
            {/* Left side - Login form */}
            <div className="flex-1 flex flex-col justify-center px-8 sm:px-12 lg:px-20 xl:px-32">
                <div className="max-w-md">
                    {/* Logo */}
                    <div className="flex items-center gap-3 mb-12">
                        <div className="w-12 h-12 bg-orange-500 rounded-sm flex items-center justify-center">
                            <Truck className="w-7 h-7 text-white" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-white tracking-tight">LoadTMS</h1>
                            <p className="text-xs text-zinc-500 font-mono uppercase tracking-widest">Dispatch Command Center</p>
                        </div>
                    </div>

                    {/* Welcome text */}
                    <div className="mb-10">
                        <h2 className="text-4xl font-bold text-white tracking-tight mb-4">
                            Welcome Back
                        </h2>
                        <p className="text-zinc-400 text-lg">
                            Sign in to access your logistics command center. Manage loads, track shipments, and optimize your fleet operations.
                        </p>
                    </div>

                    {/* Google Sign In Button */}
                    <Button
                        onClick={login}
                        className="w-full h-12 bg-white hover:bg-zinc-100 text-zinc-900 font-semibold rounded-sm flex items-center justify-center gap-3 transition-all duration-200 hover:-translate-y-0.5"
                        data-testid="google-login-btn"
                    >
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                            <path
                                fill="#4285F4"
                                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                            />
                            <path
                                fill="#34A853"
                                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                            />
                            <path
                                fill="#FBBC05"
                                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                            />
                            <path
                                fill="#EA4335"
                                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                            />
                        </svg>
                        Continue with Google
                        <ArrowRight className="w-4 h-4" />
                    </Button>

                    <p className="mt-6 text-center text-sm text-zinc-500">
                        By signing in, you agree to our Terms of Service and Privacy Policy
                    </p>
                </div>
            </div>

            {/* Right side - Features (hidden on mobile) */}
            <div className="hidden lg:flex lg:flex-1 items-center justify-center p-12">
                <div className="max-w-lg">
                    <div className="grid grid-cols-2 gap-6">
                        {features.map((feature, index) => (
                            <div
                                key={index}
                                className="p-6 bg-black/40 backdrop-blur-sm border border-white/10 rounded-sm hover:border-orange-500/50 transition-colors duration-300"
                            >
                                <feature.icon className="w-8 h-8 text-orange-500 mb-4" />
                                <h3 className="text-white font-semibold mb-2">{feature.title}</h3>
                                <p className="text-zinc-400 text-sm">{feature.description}</p>
                            </div>
                        ))}
                    </div>

                    <div className="mt-10 p-6 bg-orange-500/10 border border-orange-500/30 rounded-sm">
                        <p className="text-orange-400 text-sm font-medium mb-1">TRUSTED BY DISPATCHERS</p>
                        <p className="text-white text-lg">
                            "LoadTMS streamlined our operations and increased our load capacity by 40%."
                        </p>
                        <p className="text-zinc-500 text-sm mt-3">— Fleet Manager, Midwest Logistics</p>
                    </div>
                </div>
            </div>
        </div>
    );
}
